/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path apihooks
 * @description generic useHttpEnpoint
 */
import axios, { AxiosRequestConfig } from "axios";
import { useMutation, UseMutationOptions, useQuery } from "react-query";
const getUrlParamsString = <T extends Record<string, string>>(params: T) => {
  const urlParams = new URLSearchParams(params);
  const string = urlParams.toString();
  return string ? `?${string}` : "";
};
const useAxios = (request: AxiosRequestConfig) => async () => {
  const response = await axios(request);
  return response.data;
};
type Result = any;
type Error = any;
/**
 * Provides GET, POST, PUT and DELETE methods to certain API endpoint.
 *
 * @param endpoint
 * @returns
 */
const useHttpEndpoint = <T>(endpoint: string) => {
  return {
    useGet: (
      id: string = "",
      query: Record<string, string>,
      disabled?: boolean
    ) =>
      useQuery(
        `${endpoint}-${id}${getUrlParamsString(query)}`,
        useAxios({
          method: "GET",
          url: `${endpoint}/${id}${getUrlParamsString(query)}`,
        }),
        { enabled: !disabled }
      ),
    usePost: (body: T, options?: UseMutationOptions) =>
      useMutation<Result, Error>(
        `${endpoint}-${JSON.stringify(body)}`,
        useAxios({ url: endpoint, method: "POST", data: body }),
        options
      ),
    usePut: (id: string, body: T, options?: UseMutationOptions) =>
      useMutation<Result, Error>(
        `${endpoint}-${JSON.stringify(id)}-${JSON.stringify(body)}`,
        useAxios({ method: "PUT", data: body, url: `${endpoint}/${id}` }),
        options
      ),
    useDelete: (id: string, options?: UseMutationOptions) =>
      useMutation(
        `${endpoint}-${JSON.stringify(id)}`,
        useAxios({ method: "DELETE", url: `${endpoint}/${id}` }),
        options
      ),
  };
};
export default useHttpEndpoint;
